// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState } from 'react';
import styled from '@emotion/styled';
import FlowSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import { spacing } from 'presentations/views/styles';
import { LoadingComponent } from 'presentations/views/components/molecules/ServiceListItem';
import { Role } from 'domains/models/team_user/role';
import { ViewerItem } from 'presentations/api/schema/user';
import { WebhookUserListItem } from 'presentations/api/schema/webhook_endpoint';
import WebhookListItem from '../../molecules/WebhookListItem';
import { WebhookEndpointStatus } from 'domains/models/webhook_endpoint/webhook_endpoint_status';
import PlanUpgradeDialog from '../PlanUpgradeDialog';

export interface Props {
  webhookEndpoints: WebhookUserListItem[];
  onSelectMenu?: any;
  onClickSecret?: any;
  onClosePlanUpgrade?: any;
  viewer: ViewerItem;
  isLoading?: boolean;
  isShowingPlanUpgrade: boolean;
}

export enum WebhookOptionIndex {
  edit = 0,
  activation = 1,
  roll = 2,
  delete = 3,
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [
    anchorWebhook,
    setAnchorWebhook,
  ] = useState<null | WebhookUserListItem>(null);

  const onCloseMenu = () => {
    setAnchor(null);
    setAnchorWebhook(null);
  };
  const onClickMenu = (e: any, webhook: WebhookUserListItem) => {
    e.preventDefault();
    setAnchor(e.currentTarget);
    setAnchorWebhook(webhook);
  };
  const onSelectMenu = async (selectedIndex: number) => {
    if (!anchorWebhook) return;
    setAnchor(null);
    props.onSelectMenu(anchorWebhook, selectedIndex);
  };

  const renderMenu = (webhook: WebhookUserListItem, role: Role) => {
    if (!anchorWebhook) return null;
    if (anchorWebhook.id !== webhook.id) return null;
    if (role > Role.Admin) return null;

    const isEnabled = webhook.status === WebhookEndpointStatus.Enable;
    const options = [];
    options.push({
      icon: 'edit',
      label: t('Edit'),
    });
    options.push({
      icon: isEnabled ? 'toggle-switch' : 'toggle-switch-off',
      label: t(isEnabled ? 'Deactivate' : 'Activate'),
    });
    options.push({
      icon: 'reload',
      label: t('Roll'),
    });
    options.push({
      icon: 'delete',
      label: t('Delete'),
    });
    return (
      <FlowSettingMenu
        onCloseMenu={onCloseMenu}
        onSelectMenu={onSelectMenu}
        anchor={anchor}
        options={options}
      />
    );
  };

  return (
    <Container>
      {props.isLoading && (
        <>
          <ServiceListItemWrap>
            <LoadingComponent />
          </ServiceListItemWrap>
          <ServiceListItemWrap>
            <LoadingComponent />
          </ServiceListItemWrap>
          <ServiceListItemWrap>
            <LoadingComponent />
          </ServiceListItemWrap>
        </>
      )}
      {props.webhookEndpoints.map((webhookEndpoint) => {
        return (
          <ServiceListItemWrap key={webhookEndpoint.id}>
            <WebhookListItem
              teamUid={props.viewer.team!.uid}
              webhookEndpoint={webhookEndpoint}
              onClickMenu={onClickMenu}
              onClickSecret={props.onClickSecret}
              isLoading={props.isLoading}
              role={props.viewer.role}
            />
            {renderMenu(webhookEndpoint, props.viewer.role)}
          </ServiceListItemWrap>
        );
      })}
      {props.webhookEndpoints.length === 0 &&
        !props.isLoading &&
        t(`Empty for webhook`)}

      {props.isShowingPlanUpgrade && (
        <PlanUpgradeDialog text={null} onClose={props.onClosePlanUpgrade} />
      )}
    </Container>
  );
}

const ServiceListItemWrap = styled.div`
  margin: 0 auto ${spacing(20)};
  :last-child {
    margin-bottom: 0;
  }
`;
const Container = styled.div`
  width: 100%;
`;
